/* eslint-disable react-hooks/exhaustive-deps */
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import ErrorIcon from "@mui/icons-material/Error";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentShell from "components/ContentShell";
import { InputShell } from "components/FormInputShell";
import { SimpleMultiSelectInput } from "components/FormMultiSelectInput";
import { SimpleSelectInput } from "components/FormSelectInput";
import { SimpleTextInput } from "components/FormTextInput";
import SimpleSwitch from "components/Switch";
import configData from "config/config.json";
import { useSnackbar } from "hooks/useSnackbar";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  create,
  generateHexSecret,
  getPlatformInfo,
  getPlatformNames,
  revealPlatformPassword,
  update,
} from "services/platformService";
import useStyles from "styles/platform/platformEditor";
import { deepCopy } from "utils/miscUtils";

const method = {
  url: "",
  method: "",
};
const _defaultRotationConfig = {
  enabled: false,
  retryableErrorCodes: [] as number[],
  threshold: 3,
  redisTtl: 86400,
  maxRotations: 3,
};

const _defaultQueueRetryConfig = {
  attempts: 3,
  useExponentialWithCap: true,
  baseDelayMs: 100,
  maxDelayMs: 600000,
};

const _defaultFailureRetryConfig = {
  enabled: false,
  maxRetries: 1,
  delayMs: 300,
  retryCodes: {
    bet: [] as number[],
    win: [] as number[],
    cancelBet: [] as number[],
  },
  cancelOnFailureCodes: {
    bet: [] as number[],
  },
};

const _platformDetails = {
  platformName: "",
  strategyType: "",
  username: "",
  password: "",
  isRedirect: false,
  apiTimeoutMs: "",
  extraData: "",
  roundIdRotationConfig: { ..._defaultRotationConfig },
  failureRetryConfig: {
    ..._defaultFailureRetryConfig,
    retryCodes: { ..._defaultFailureRetryConfig.retryCodes },
  },
  queueRetryConfigEnabled: false,
  queueRetryConfig: { ..._defaultQueueRetryConfig },
  completeGameConfigEnabled: false,
  completeGameConfig: {
    callOnCreditFailure: false,
    roundFinishedOnLastCredit: false,
  },
  betRequest: method,
  winRequest: method,
  balanceRequest: method,
  refundRequest: method,
  loginFromTokenRequest: method,
  completeGameRequest: method,
  voidGameRequest: method,
  reconcileRequest: method,
};

const defaultRequestMethods = [
  "betRequest",
  "winRequest",
  "balanceRequest",
  "refundRequest",
  "loginFromTokenRequest",
  "completeGameRequest",
  "voidGameRequest",
  "reconcileRequest",
];

const optionalRequestMethods = new Set([
  "completeGameRequest",
  "voidGameRequest",
  "reconcileRequest",
]);

const PlatformEditor = () => {
  const styles = useStyles();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();
  const id = location.pathname.split("/").slice(-1)[0];
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [platformDetails, setPlatformDetails] = useState(_platformDetails);
  const [hasStoredPassword, setHasStoredPassword] = useState(false);
  const [isEditingPassword, setIsEditingPassword] = useState(false);
  const [revealedPassword, setRevealedPassword] = useState(null);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [revealLoading, setRevealLoading] = useState(false);
  const [removePassword, setRemovePassword] = useState(false);
  const [jsonError, setJsonError] = useState("");
  const [isJsonValid, setIsJsonValid] = useState(true);
  const [rotationInfoOpen, setRotationInfoOpen] = useState(false);
  const [retryInfoOpen, setRetryInfoOpen] = useState(false);
  const [hexSecretDialogOpen, setHexSecretDialogOpen] = useState(false);
  const [generatedSecret, setGeneratedSecret] = useState("");
  const [generatingSecret, setGeneratingSecret] = useState(false);
  const [options, setOptions] = useState({
    httpMethodOptions: [
      { id: "POST", label: "POST" },
      { id: "GET", label: "GET" },
      { id: "DELETE", label: "DELETE" },
      { id: "PUT", label: "PUT" },
      { id: "PATCH", label: "PATCH" },
    ],
    requiredMethods: [],
    requiredMethodOptions: defaultRequestMethods.map((e) => ({
      id: e,
      label:
        e.at(0).toUpperCase() +
        e.slice(1) +
        (optionalRequestMethods.has(e) ? " (Optional)" : ""),
    })),
    strategyTypeOptions: [{ id: "", label: "None (clear)" }],
  });

  const cancel = () => navigate(configData.routes.platforms.platforms);

  const validateJSON = (jsonString: any) => {
    if (!jsonString || jsonString.trim() === "") {
      setJsonError("");
      setIsJsonValid(true);
      return true;
    }

    try {
      JSON.parse(jsonString);
      setJsonError("");
      setIsJsonValid(true);
      return true;
    } catch (error) {
      setJsonError(error.message);
      setIsJsonValid(false);
      return false;
    }
  };

  const handleFormatJSON = () => {
    if (!platformDetails.extraData || platformDetails.extraData.trim() === "") {
      return;
    }

    try {
      const parsed = JSON.parse(platformDetails.extraData);
      const formatted = JSON.stringify(parsed, null, 2);
      setPlatformDetails((state) => ({
        ...state,
        extraData: formatted,
      }));
      setJsonError("");
      setIsJsonValid(true);
      showSnackbar("JSON formatted successfully", "success");
    } catch (error) {
      showSnackbar("Cannot format invalid JSON", "error");
    }
  };

  const handleGenerateSecret = async () => {
    try {
      setGeneratingSecret(true);
      const resp = await generateHexSecret();
      setGeneratedSecret(resp.data.secret);
      setHexSecretDialogOpen(true);
    } catch (error) {
      showSnackbar(error.message || "Failed to generate secret", "error");
    } finally {
      setGeneratingSecret(false);
    }
  };

  const handleCopySecret = async () => {
    try {
      await navigator.clipboard.writeText(generatedSecret);
      showSnackbar("Secret copied to clipboard", "success");
    } catch {
      showSnackbar("Failed to copy to clipboard", "error");
    }
  };

  const handleClearJSON = () => {
    setPlatformDetails((state) => ({
      ...state,
      extraData: "",
    }));
    setJsonError("");
    setIsJsonValid(true);
  };

  const submit = async () => {
    // Validate extraData JSON if provided
    if (platformDetails.extraData && platformDetails.extraData.trim() !== "") {
      if (!validateJSON(platformDetails.extraData)) {
        showSnackbar("Extra Data must be valid JSON or empty", "error");
        return;
      }
    }

    try {
      setLoading(true);
      let response;

      // Prepare payload - convert extraData string to object if valid JSON
      const payload = deepCopy(platformDetails);

      // Remove strategyType from payload if not selected
      if (!payload.strategyType) {
        delete payload.strategyType;
      }

      // Remove username if cleared — BE will $unset it
      if (!payload.username) delete payload.username;

      // Password: tri-state — removed / new value typed / untouched (never resend the old one)
      if (removePassword) {
        payload.removePassword = true;
        delete payload.password;
      } else if (!payload.password) {
        delete payload.password;
      }

      // Remove request methods that are not selected — covers unchecked optional fields
      // even when they have existing data loaded from the DB
      for (const field of defaultRequestMethods) {
        if (!options.requiredMethods.includes(field)) {
          delete payload[field];
        }
      }

      if (payload.extraData && payload.extraData.trim() !== "") {
        try {
          payload.extraData = JSON.parse(payload.extraData);
        } catch (e) {
          showSnackbar("Invalid JSON in Extra Data", "error");
          setLoading(false);
          return;
        }
      } else {
        // Remove extraData if empty
        delete payload.extraData;
      }

      // Coerce apiTimeoutMs to a number, or remove if cleared
      if (
        payload.apiTimeoutMs !== "" &&
        payload.apiTimeoutMs !== undefined &&
        payload.apiTimeoutMs !== null
      ) {
        payload.apiTimeoutMs = parseInt(payload.apiTimeoutMs, 10) || undefined;
        if (payload.apiTimeoutMs === undefined) delete payload.apiTimeoutMs;
      } else {
        delete payload.apiTimeoutMs;
      }

      // Ensure rotation config numeric fields are numbers
      if (payload.roundIdRotationConfig) {
        payload.roundIdRotationConfig.threshold =
          parseInt(payload.roundIdRotationConfig.threshold, 10) || 3;
        payload.roundIdRotationConfig.redisTtl =
          parseInt(payload.roundIdRotationConfig.redisTtl, 10) || 86400;
        payload.roundIdRotationConfig.maxRotations =
          parseInt(payload.roundIdRotationConfig.maxRotations, 10) || 3;
        payload.roundIdRotationConfig.retryableErrorCodes = (
          payload.roundIdRotationConfig.retryableErrorCodes || []
        )
          .map(Number)
          .filter((n) => !isNaN(n));
      }

      // Include queueRetryConfig only when the override switch is on
      if (payload.queueRetryConfigEnabled) {
        payload.queueRetryConfig = {
          attempts: parseInt(payload.queueRetryConfig.attempts, 10) || 1,
          useExponentialWithCap:
            !!payload.queueRetryConfig.useExponentialWithCap,
          baseDelayMs: parseInt(payload.queueRetryConfig.baseDelayMs, 10) || 1,
          maxDelayMs: parseInt(payload.queueRetryConfig.maxDelayMs, 10) || 1,
        };
      } else {
        delete payload.queueRetryConfig;
      }
      delete payload.queueRetryConfigEnabled;

      // Include completeGameConfig only when the override switch is on
      if (!payload.completeGameConfigEnabled) {
        delete payload.completeGameConfig;
      }
      delete payload.completeGameConfigEnabled;

      // Ensure failure retry config numeric fields are numbers
      if (payload.failureRetryConfig) {
        payload.failureRetryConfig.maxRetries =
          parseInt(payload.failureRetryConfig.maxRetries, 10) || 1;
        payload.failureRetryConfig.delayMs =
          parseInt(payload.failureRetryConfig.delayMs, 10) || 300;
        payload.failureRetryConfig.retryCodes = {
          bet: (payload.failureRetryConfig.retryCodes?.bet || [])
            .map(Number)
            .filter((n) => !isNaN(n)),
          win: (payload.failureRetryConfig.retryCodes?.win || [])
            .map(Number)
            .filter((n) => !isNaN(n)),
          cancelBet: (payload.failureRetryConfig.retryCodes?.cancelBet || [])
            .map(Number)
            .filter((n) => !isNaN(n)),
        };
        payload.failureRetryConfig.cancelOnFailureCodes = {
          bet: (payload.failureRetryConfig.cancelOnFailureCodes?.bet || [])
            .map(Number)
            .filter((n) => !isNaN(n)),
        };
      }

      if (id === "create") {
        response = await create(payload);
      } else {
        const { _id, id, updatedAt, createdAt, isActive, ...rest } = payload;
        response = await update(rest, _id);
      }

      showSnackbar(response.message, "success");
      setLoading(false);
      navigate(configData.routes.platforms.platforms);
    } catch (error) {
      showSnackbar(error.message, "error");
      setLoading(false);
    }
  };

  const onChange = async (e) => {
    const { name, value } = e?.target;

    if (name === "requiredMethods") {
      setOptions((op) => ({ ...op, requiredMethods: value }));
    } else {
      setPlatformDetails((state) => ({
        ...state,
        [name]: name === "isRedirect" ? !state.isRedirect : value,
      }));

      // Validate JSON in real-time for extraData field
      if (name === "extraData") {
        validateJSON(value);
      }
    }
  };

  const handleTogglePasswordVisibility = async () => {
    // Already fetched once - just toggle visibility, no re-fetch.
    if (revealedPassword) {
      setIsPasswordVisible((visible) => !visible);
      return;
    }

    try {
      setRevealLoading(true);
      const resp = await revealPlatformPassword(id);
      setRevealedPassword(resp.data.password);
      setIsPasswordVisible(true);
    } catch (error) {
      showSnackbar(error.message, "error");
    } finally {
      setRevealLoading(false);
    }
  };

  const handleChangePassword = () => {
    setIsEditingPassword(true);
    setRevealedPassword(null);
    setIsPasswordVisible(false);
    setRemovePassword(false);
    setPlatformDetails((state) => ({ ...state, password: "" }));
  };

  const fetchPlatformInfo = async () => {
    try {
      setLoading(true);
      const resp = await getPlatformInfo(id);
      const data = resp.data;

      // password is never returned by this endpoint anymore (see hasPassword below) -
      // the field always represents "a new value being typed", starts empty.
      setHasStoredPassword(!!data.hasPassword);
      delete data.hasPassword;
      data.password = "";

      // Format extraData if it's an object
      if (data.extraData && typeof data.extraData === "object") {
        data.extraData = JSON.stringify(data.extraData, null, 2);
      }

      // Merge rotation config with defaults
      data.roundIdRotationConfig = {
        ..._defaultRotationConfig,
        ...(data.roundIdRotationConfig || {}),
      };

      // Merge queue retry config with defaults; flip override flag on only if present
      data.queueRetryConfigEnabled = !!data.queueRetryConfig;
      data.queueRetryConfig = {
        ..._defaultQueueRetryConfig,
        ...(data.queueRetryConfig || {}),
      };

      // Merge complete game config with defaults; flip override flag on only if present
      data.completeGameConfigEnabled = !!data.completeGameConfig;
      data.completeGameConfig = {
        callOnCreditFailure: false,
        roundFinishedOnLastCredit: false,
        ...(data.completeGameConfig || {}),
      };

      // Merge failure retry config with defaults
      data.failureRetryConfig = {
        ..._defaultFailureRetryConfig,
        ...(data.failureRetryConfig || {}),
        retryCodes: {
          ..._defaultFailureRetryConfig.retryCodes,
          ...(data.failureRetryConfig?.retryCodes || {}),
        },
        cancelOnFailureCodes: {
          ..._defaultFailureRetryConfig.cancelOnFailureCodes,
          ...(data.failureRetryConfig?.cancelOnFailureCodes || {}),
        },
      };

      setPlatformDetails(data);

      // Validate extraData if present
      if (data.extraData) {
        validateJSON(data.extraData);
      }

      if (resp && resp?.data) {
        const keys = Object.keys(resp.data);
        keys.forEach((v) => {
          if (
            defaultRequestMethods.includes(v) &&
            !options.requiredMethods.includes(v)
          ) {
            setOptions((state) => ({
              ...state,
              requiredMethods: [...state.requiredMethods, v],
            }));
          }
        });
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar(error.message);
    }
  };

  const onRequestChange = (e, request) => {
    const { name, value } = e.target;
    setPlatformDetails((state) => ({
      ...state,
      [request]: {
        ...state[request],
        [name]: value,
      },
    }));
  };

  const onRotationConfigChange = (name, value) => {
    setPlatformDetails((state) => ({
      ...state,
      roundIdRotationConfig: {
        ...state.roundIdRotationConfig,
        [name]: value,
      },
    }));
  };

  const onRotationToggle = () => {
    setPlatformDetails((state) => ({
      ...state,
      roundIdRotationConfig: {
        ...state.roundIdRotationConfig,
        enabled: !state.roundIdRotationConfig.enabled,
      },
    }));
  };

  const onFailureRetryConfigChange = (name: string, value: any) => {
    setPlatformDetails((state) => ({
      ...state,
      failureRetryConfig: {
        ...state.failureRetryConfig,
        [name]: value,
      },
    }));
  };

  const onFailureRetryToggle = () => {
    setPlatformDetails((state) => ({
      ...state,
      failureRetryConfig: {
        ...state.failureRetryConfig,
        enabled: !state.failureRetryConfig.enabled,
      },
    }));
  };

  const onQueueRetryConfigToggle = () => {
    setPlatformDetails((state) => ({
      ...state,
      queueRetryConfigEnabled: !state.queueRetryConfigEnabled,
    }));
  };

  const onQueueRetryConfigChange = (name: string, value: any) => {
    setPlatformDetails((state) => ({
      ...state,
      queueRetryConfig: {
        ...state.queueRetryConfig,
        [name]: value,
      },
    }));
  };

  const onCompleteGameConfigToggle = () => {
    setPlatformDetails((state) => ({
      ...state,
      completeGameConfigEnabled: !state.completeGameConfigEnabled,
    }));
  };

  const onCompleteGameConfigChange = (name: string, value: boolean) => {
    setPlatformDetails((state) => ({
      ...state,
      completeGameConfig: {
        ...state.completeGameConfig,
        [name]: value,
      },
    }));
  };

  const onFailureRetryCodesChange = (
    operation: "bet" | "win" | "cancelBet",
    codes: number[],
  ) => {
    setPlatformDetails((state) => ({
      ...state,
      failureRetryConfig: {
        ...state.failureRetryConfig,
        retryCodes: {
          ...state.failureRetryConfig.retryCodes,
          [operation]: codes,
        },
      },
    }));
  };

  const onCancelOnFailureCodesChange = (operation: "bet", codes: number[]) => {
    setPlatformDetails((state) => ({
      ...state,
      failureRetryConfig: {
        ...state.failureRetryConfig,
        cancelOnFailureCodes: {
          ...state.failureRetryConfig.cancelOnFailureCodes,
          [operation]: codes,
        },
      },
    }));
  };

  const fetchStrategyTypeOptions = async () => {
    try {
      const resp = await getPlatformNames();
      const names: string[] = resp?.data || [];
      setOptions((state) => ({
        ...state,
        strategyTypeOptions: [
          { id: "", label: "None (clear)" },
          ...names.map((name) => ({
            id: name,
            label: name.at(0).toUpperCase() + name.slice(1),
          })),
        ],
      }));
    } catch (error) {
      showSnackbar(error.message || "Failed to load strategy types", "error");
    }
  };

  useEffect(() => {
    fetchStrategyTypeOptions();
    if (id !== "create") fetchPlatformInfo();
  }, []);

  return (
    <ContentShell
      showBackButton
      onBackButtonClick={cancel}
      title={`${id === "create" ? "Create" : "Edit"} Platform`}
      loading={loading}
      actions={
        <Box className={styles.buttonContainer}>
          <Button
            type="submit"
            variant="contained"
            style={{ backgroundColor: "red", color: "white" }}
            onClick={cancel}
          >
            Cancel
          </Button>
          <Button type="submit" variant="contained" onClick={submit}>
            Save
          </Button>
        </Box>
      }
    >
      <Box className={styles.container}>
        <Box className={styles.firstFormContainer}>
          <Box className={styles.field}>
            <SimpleTextInput
              label="Platform Name"
              name="platformName"
              value={platformDetails.platformName}
              onChange={onChange}
            />
          </Box>
          <Box className={styles.field}>
            <SimpleSelectInput
              label="Strategy Type"
              name="strategyType"
              value={platformDetails.strategyType}
              options={options.strategyTypeOptions}
              onChange={onChange}
              placeholdertext="Select strategy type (optional)"
            />
          </Box>
          <Box className={styles.field}>
            <SimpleTextInput
              label="Username (Optional)"
              name="username"
              value={platformDetails.username}
              onChange={onChange}
            />
          </Box>
          <Box className={styles.field}>
            {hasStoredPassword && !isEditingPassword ? (
              <InputShell label="Password (Optional)" name="password">
                <TextField
                  variant="outlined"
                  fullWidth
                  value={
                    removePassword
                      ? "Removing on save"
                      : isPasswordVisible && revealedPassword
                        ? revealedPassword
                        : "••••••••"
                  }
                  title={removePassword ? "Removing on save" : undefined}
                  InputProps={{
                    readOnly: true,
                    style: { height: 38 },
                    endAdornment: (
                      <InputAdornment position="end">
                        {removePassword ? (
                          <IconButton
                            size="small"
                            title="Undo remove"
                            onClick={() => setRemovePassword(false)}
                          >
                            <CloseIcon
                              fontSize="small"
                              style={{ transform: "rotate(45deg)" }}
                            />
                          </IconButton>
                        ) : (
                          <>
                            <IconButton
                              size="small"
                              title={
                                isPasswordVisible
                                  ? "Hide password"
                                  : "Reveal password"
                              }
                              onClick={handleTogglePasswordVisibility}
                              disabled={revealLoading}
                            >
                              {revealLoading ? (
                                <CircularProgress size={16} />
                              ) : isPasswordVisible ? (
                                <VisibilityOff fontSize="small" />
                              ) : (
                                <Visibility fontSize="small" />
                              )}
                            </IconButton>
                            <IconButton
                              size="small"
                              title="Change password"
                              onClick={handleChangePassword}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              title="Remove password"
                              onClick={() => setRemovePassword(true)}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              </InputShell>
            ) : (
              <SimpleTextInput
                label="Password (Optional)"
                name="password"
                value={platformDetails.password}
                onChange={onChange}
              />
            )}
          </Box>

          <Box className={styles.field}>
            <SimpleMultiSelectInput
              label="Select Required Methods"
              name="requiredMethods"
              value={options.requiredMethods}
              options={options.requiredMethodOptions}
              onChange={onChange}
            />
          </Box>
          <Box className={styles.field}>
            <SimpleSwitch
              legendText={"Is Redirect"}
              name="isRedirect"
              value={platformDetails.isRedirect}
              label={platformDetails.isRedirect ? "YES" : "NO"}
              onChange={onChange}
            />
          </Box>
          <Box className={styles.field}>
            <SimpleTextInput
              label="Client HTTP Timeout (ms) (Optional)"
              name="apiTimeoutMs"
              type="number"
              value={String(platformDetails.apiTimeoutMs ?? "")}
              onChange={onChange}
              inputProps={{ min: 1, step: 1 }}
            />
          </Box>

          {/* Generate Hex Secret */}
          <Box className={styles.field}>
            <Button
              variant="outlined"
              startIcon={
                generatingSecret ? (
                  <CircularProgress size={16} />
                ) : (
                  <VpnKeyIcon />
                )
              }
              onClick={handleGenerateSecret}
              disabled={generatingSecret}
              size="small"
            >
              Generate Hex Secret
            </Button>
          </Box>

          {/* Extra Data JSON Editor */}
          <Box className={styles.jsonEditorContainer}>
            <Box className={styles.jsonEditorHeader}>
              <Box className={styles.jsonEditorLabel}>
                <span>Extra Data (JSON) - Optional</span>
                {platformDetails.extraData &&
                  platformDetails.extraData.trim() !== "" && (
                    <>
                      {isJsonValid ? (
                        <CheckCircleIcon
                          className={styles.jsonValidIcon}
                          style={{ color: "#4caf50" }}
                        />
                      ) : (
                        <ErrorIcon
                          className={styles.jsonValidIcon}
                          style={{ color: "#f44336" }}
                        />
                      )}
                    </>
                  )}
              </Box>
              <Box className={styles.jsonEditorActions}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<FormatAlignJustifyIcon />}
                  onClick={handleFormatJSON}
                  disabled={!platformDetails.extraData || !isJsonValid}
                >
                  Format
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={handleClearJSON}
                  disabled={!platformDetails.extraData}
                >
                  Clear
                </Button>
              </Box>
            </Box>
            <TextField
              fullWidth
              multiline
              minRows={12}
              maxRows={30}
              name="extraData"
              value={platformDetails.extraData}
              onChange={onChange}
              placeholder={
                '{\n  "key": "value",\n  "example": {\n    "nested": true\n  }\n}'
              }
              variant="outlined"
              error={!isJsonValid && platformDetails.extraData?.trim() !== ""}
              InputProps={{
                className: styles.jsonTextField,
                style: { resize: "vertical" },
              }}
              sx={{
                "& .MuiInputBase-root": {
                  resize: "vertical",
                  overflow: "auto",
                },
              }}
            />
            {jsonError && (
              <Box className={styles.jsonError}>
                <ErrorIcon style={{ fontSize: "14px" }} />
                <span>{jsonError}</span>
              </Box>
            )}
            {!jsonError && (
              <Typography className={styles.jsonHelperText}>
                Enter valid JSON data. Use the Format button to auto-format your
                JSON. Examples: {`{"setting": "value"}`},{" "}
                {`{"config": {"enabled": true}}`}
              </Typography>
            )}
          </Box>

          {/* Round ID Rotation Config */}
          <Accordion className={styles.rotationConfigContainer}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography className={styles.rotationConfigTitle}>
                Round ID Rotation Config
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setRotationInfoOpen(true);
                  }}
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Box>
              <SimpleSwitch
                legendText="Enable Round ID Rotation"
                name="roundIdRotationEnabled"
                value={platformDetails.roundIdRotationConfig.enabled}
                label={
                  platformDetails.roundIdRotationConfig.enabled ? "YES" : "NO"
                }
                onChange={onRotationToggle}
              />
              {platformDetails.roundIdRotationConfig.enabled && (
                <Box className={styles.rotationConfigFields}>
                  <Box className={styles.field}>
                    <SimpleTextInput
                      label="Consecutive Error Threshold"
                      name="threshold"
                      type="number"
                      value={String(
                        platformDetails.roundIdRotationConfig.threshold,
                      )}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onRotationConfigChange(
                          "threshold",
                          parseInt(e.target.value) || 1,
                        )
                      }
                      inputProps={{ min: 1, step: 1 }}
                    />
                  </Box>
                  <Box className={styles.field}>
                    <SimpleTextInput
                      label="Redis TTL (seconds)"
                      name="redisTtl"
                      type="number"
                      value={String(
                        platformDetails.roundIdRotationConfig.redisTtl,
                      )}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onRotationConfigChange(
                          "redisTtl",
                          parseInt(e.target.value) || 1,
                        )
                      }
                      inputProps={{ min: 1, step: 1 }}
                    />
                  </Box>
                  <Box className={styles.field}>
                    <SimpleTextInput
                      label="Max Rotations Per Round"
                      name="maxRotations"
                      type="number"
                      value={String(
                        platformDetails.roundIdRotationConfig.maxRotations,
                      )}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onRotationConfigChange(
                          "maxRotations",
                          parseInt(e.target.value) || 1,
                        )
                      }
                      inputProps={{ min: 1, step: 1 }}
                    />
                  </Box>
                  <Box>
                    <InputShell
                      label="Retryable Error Codes"
                      name="retryableErrorCodes"
                    >
                      <Autocomplete
                        multiple
                        freeSolo
                        options={[]}
                        value={platformDetails.roundIdRotationConfig.retryableErrorCodes.map(
                          String,
                        )}
                        onChange={(_event: any, newValue: string[]) => {
                          const numericCodes = newValue
                            .map((v) => parseInt(v, 10))
                            .filter((n) => !isNaN(n));
                          onRotationConfigChange(
                            "retryableErrorCodes",
                            numericCodes,
                          );
                        }}
                        renderTags={(value: string[], getTagProps: any) =>
                          value.map((option: string, index: number) => (
                            <Chip
                              variant="outlined"
                              label={option}
                              size="small"
                              {...getTagProps({ index })}
                              key={option}
                            />
                          ))
                        }
                        renderInput={(params: any) => (
                          <TextField
                            {...params}
                            placeholder="Type error code and press Enter"
                            variant="outlined"
                            InputProps={{
                              ...params.InputProps,
                              style: { height: "auto", minHeight: 38 },
                            }}
                          />
                        )}
                        sx={{ minWidth: 220 }}
                      />
                    </InputShell>
                  </Box>
                  <Typography className={styles.rotationConfigHelperText}>
                    When enabled, the system rotates the numeric round ID after
                    the specified number of consecutive retryable errors. Total
                    rotations per round are capped by &quot;Max Rotations Per
                    Round&quot;. All generated round IDs are tracked and
                    completed at the end of the game round. Error codes should
                    be numeric (e.g. 6, 407).
                  </Typography>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Failure Retry Config */}
          <Accordion className={styles.failureRetryConfigContainer}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography className={styles.failureRetryConfigTitle}>
                Failure Retry Config
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setRetryInfoOpen(true);
                  }}
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Box>
              <SimpleSwitch
                legendText="Enable Failure Retry"
                name="failureRetryEnabled"
                value={platformDetails.failureRetryConfig.enabled}
                label={
                  platformDetails.failureRetryConfig.enabled ? "YES" : "NO"
                }
                onChange={onFailureRetryToggle}
              />
              {platformDetails.failureRetryConfig.enabled && (
                <>
                  <Box className={styles.failureRetryConfigFields}>
                    <Box className={styles.field}>
                      <SimpleTextInput
                        label="Max Retries"
                        name="maxRetries"
                        type="number"
                        value={String(
                          platformDetails.failureRetryConfig.maxRetries,
                        )}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          onFailureRetryConfigChange(
                            "maxRetries",
                            parseInt(e.target.value) || 1,
                          )
                        }
                        inputProps={{ min: 1, max: 5, step: 1 }}
                      />
                    </Box>
                    <Box className={styles.field}>
                      <SimpleTextInput
                        label="Delay (ms)"
                        name="delayMs"
                        type="number"
                        value={String(
                          platformDetails.failureRetryConfig.delayMs,
                        )}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          onFailureRetryConfigChange(
                            "delayMs",
                            parseInt(e.target.value) || 100,
                          )
                        }
                        inputProps={{ min: 100, max: 2000, step: 100 }}
                      />
                    </Box>
                    <Box>
                      <InputShell
                        label="Bet Retry Error Codes"
                        name="betRetryCodes"
                      >
                        <Autocomplete
                          multiple
                          freeSolo
                          options={[]}
                          value={platformDetails.failureRetryConfig.retryCodes.bet.map(
                            String,
                          )}
                          onChange={(_event: any, newValue: string[]) => {
                            const numericCodes = newValue
                              .map((v) => parseInt(v, 10))
                              .filter((n) => !isNaN(n));
                            onFailureRetryCodesChange("bet", numericCodes);
                          }}
                          renderTags={(value: string[], getTagProps: any) =>
                            value.map((option: string, index: number) => (
                              <Chip
                                variant="outlined"
                                label={option}
                                size="small"
                                {...getTagProps({ index })}
                                key={option}
                              />
                            ))
                          }
                          renderInput={(params: any) => (
                            <TextField
                              {...params}
                              placeholder="Type error code and press Enter"
                              variant="outlined"
                              InputProps={{
                                ...params.InputProps,
                                style: { height: "auto", minHeight: 38 },
                              }}
                            />
                          )}
                          sx={{ minWidth: 220 }}
                        />
                      </InputShell>
                    </Box>
                    <Box>
                      <InputShell
                        label="Win Retry Error Codes"
                        name="winRetryCodes"
                      >
                        <Autocomplete
                          multiple
                          freeSolo
                          options={[]}
                          value={platformDetails.failureRetryConfig.retryCodes.win.map(
                            String,
                          )}
                          onChange={(_event: any, newValue: string[]) => {
                            const numericCodes = newValue
                              .map((v) => parseInt(v, 10))
                              .filter((n) => !isNaN(n));
                            onFailureRetryCodesChange("win", numericCodes);
                          }}
                          renderTags={(value: string[], getTagProps: any) =>
                            value.map((option: string, index: number) => (
                              <Chip
                                variant="outlined"
                                label={option}
                                size="small"
                                {...getTagProps({ index })}
                                key={option}
                              />
                            ))
                          }
                          renderInput={(params: any) => (
                            <TextField
                              {...params}
                              placeholder="Type error code and press Enter"
                              variant="outlined"
                              InputProps={{
                                ...params.InputProps,
                                style: { height: "auto", minHeight: 38 },
                              }}
                            />
                          )}
                          sx={{ minWidth: 220 }}
                        />
                      </InputShell>
                    </Box>
                    <Box>
                      <InputShell
                        label="Cancel Bet Retry Error Codes"
                        name="cancelBetRetryCodes"
                      >
                        <Autocomplete
                          multiple
                          freeSolo
                          options={[]}
                          value={(
                            platformDetails.failureRetryConfig.retryCodes
                              .cancelBet || []
                          ).map(String)}
                          onChange={(_event: any, newValue: string[]) => {
                            const numericCodes = newValue
                              .map((v) => parseInt(v, 10))
                              .filter((n) => !isNaN(n));
                            onFailureRetryCodesChange(
                              "cancelBet",
                              numericCodes,
                            );
                          }}
                          renderTags={(value: string[], getTagProps: any) =>
                            value.map((option: string, index: number) => (
                              <Chip
                                variant="outlined"
                                label={option}
                                size="small"
                                {...getTagProps({ index })}
                                key={option}
                              />
                            ))
                          }
                          renderInput={(params: any) => (
                            <TextField
                              {...params}
                              placeholder="Type error code and press Enter"
                              variant="outlined"
                              InputProps={{
                                ...params.InputProps,
                                style: { height: "auto", minHeight: 38 },
                              }}
                            />
                          )}
                          sx={{ minWidth: 220 }}
                        />
                      </InputShell>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.5, display: "block" }}
                      >
                        Error codes that trigger a retry of the cancel-bet call
                        itself (distinct from &quot;Cancel On Failure Bet
                        Codes&quot; below, which decides whether to attempt a
                        cancel in the first place).
                      </Typography>
                    </Box>
                  </Box>
                  <Typography className={styles.failureRetryConfigHelperText}>
                    When enabled, the system retries failed bet/win calls when
                    the platform returns specific error codes. Retries use the
                    same request parameters (same numericRoundId). Keep max
                    retries low (1-2) and delay short (200-500ms) to avoid ALB
                    timeouts. Error codes should be numeric (e.g. 6, 407).
                  </Typography>
                </>
              )}
              <Box sx={{ mt: 2 }}>
                <InputShell
                  label="Cancel On Failure Bet Codes"
                  name="cancelOnFailureBetCodes"
                >
                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={(
                      platformDetails.failureRetryConfig.cancelOnFailureCodes
                        ?.bet || []
                    ).map(String)}
                    onChange={(_event: any, newValue: string[]) => {
                      const numericCodes = newValue
                        .map((v) => parseInt(v, 10))
                        .filter((n) => !isNaN(n));
                      onCancelOnFailureCodesChange("bet", numericCodes);
                    }}
                    renderTags={(value: string[], getTagProps: any) =>
                      value.map((option: string, index: number) => (
                        <Chip
                          variant="outlined"
                          label={option}
                          size="small"
                          {...getTagProps({ index })}
                          key={option}
                        />
                      ))
                    }
                    renderInput={(params: any) => (
                      <TextField
                        {...params}
                        placeholder="Type error code and press Enter"
                        variant="outlined"
                        InputProps={{
                          ...params.InputProps,
                          style: { height: "auto", minHeight: 38 },
                        }}
                      />
                    )}
                    sx={{ minWidth: 220 }}
                  />
                </InputShell>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  Bet error codes that trigger a CancelFundGame call after all
                  retries fail (best-effort, independent of retry being
                  enabled). Use for indeterministic platform errors (e.g.
                  GamesGlobal error code 6).
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Queue Retry Config */}
          <Accordion className={styles.rotationConfigContainer}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography className={styles.rotationConfigTitle}>
                Queue Retry Config
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <SimpleSwitch
                legendText="Override Queue Retry Defaults"
                name="queueRetryConfigEnabled"
                value={platformDetails.queueRetryConfigEnabled}
                label={platformDetails.queueRetryConfigEnabled ? "YES" : "NO"}
                onChange={onQueueRetryConfigToggle}
              />
              {platformDetails.queueRetryConfigEnabled && (
                <Box className={styles.rotationConfigFields}>
                  <Box className={styles.field}>
                    <SimpleTextInput
                      label="Attempts"
                      name="attempts"
                      type="number"
                      value={String(platformDetails.queueRetryConfig.attempts)}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onQueueRetryConfigChange(
                          "attempts",
                          parseInt(e.target.value) || 1,
                        )
                      }
                      inputProps={{ min: 1, step: 1 }}
                    />
                  </Box>
                  <Box className={styles.field}>
                    <SimpleSwitch
                      legendText="Use Exponential Backoff With Cap"
                      name="useExponentialWithCap"
                      value={
                        platformDetails.queueRetryConfig.useExponentialWithCap
                      }
                      label={
                        platformDetails.queueRetryConfig.useExponentialWithCap
                          ? "YES"
                          : "NO"
                      }
                      onChange={() =>
                        onQueueRetryConfigChange(
                          "useExponentialWithCap",
                          !platformDetails.queueRetryConfig
                            .useExponentialWithCap,
                        )
                      }
                    />
                  </Box>
                  <Box className={styles.field}>
                    <SimpleTextInput
                      label="Base Delay (ms)"
                      name="baseDelayMs"
                      type="number"
                      value={String(
                        platformDetails.queueRetryConfig.baseDelayMs,
                      )}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onQueueRetryConfigChange(
                          "baseDelayMs",
                          parseInt(e.target.value) || 1,
                        )
                      }
                      inputProps={{ min: 1, step: 1 }}
                    />
                  </Box>
                  <Box className={styles.field}>
                    <SimpleTextInput
                      label="Max Delay (ms)"
                      name="maxDelayMs"
                      type="number"
                      value={String(
                        platformDetails.queueRetryConfig.maxDelayMs,
                      )}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onQueueRetryConfigChange(
                          "maxDelayMs",
                          parseInt(e.target.value) || 1,
                        )
                      }
                      inputProps={{ min: 1, step: 1 }}
                    />
                  </Box>
                  <Typography className={styles.rotationConfigHelperText}>
                    Controls BullMQ job retry backoff for this platform's
                    win/credit queue jobs. When off, the system default
                    (config.queue.retryAttempts) is used instead.
                  </Typography>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Complete Game Config */}
          <Accordion className={styles.rotationConfigContainer}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography className={styles.rotationConfigTitle}>
                Complete Game Config
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <SimpleSwitch
                legendText="Override Complete Game Behavior"
                name="completeGameConfigEnabled"
                value={platformDetails.completeGameConfigEnabled}
                label={platformDetails.completeGameConfigEnabled ? "YES" : "NO"}
                onChange={onCompleteGameConfigToggle}
              />
              {platformDetails.completeGameConfigEnabled && (
                <Box className={styles.rotationConfigFields}>
                  <Box className={styles.field}>
                    <SimpleSwitch
                      legendText="Call CompleteGame On Credit Failure"
                      name="callOnCreditFailure"
                      value={
                        platformDetails.completeGameConfig.callOnCreditFailure
                      }
                      label={
                        platformDetails.completeGameConfig.callOnCreditFailure
                          ? "YES"
                          : "NO"
                      }
                      onChange={() =>
                        onCompleteGameConfigChange(
                          "callOnCreditFailure",
                          !platformDetails.completeGameConfig
                            .callOnCreditFailure,
                        )
                      }
                    />
                  </Box>
                  <Box className={styles.field}>
                    <SimpleSwitch
                      legendText="Round Finished On Last Credit"
                      name="roundFinishedOnLastCredit"
                      value={
                        platformDetails.completeGameConfig
                          .roundFinishedOnLastCredit
                      }
                      label={
                        platformDetails.completeGameConfig
                          .roundFinishedOnLastCredit
                          ? "YES"
                          : "NO"
                      }
                      onChange={() =>
                        onCompleteGameConfigChange(
                          "roundFinishedOnLastCredit",
                          !platformDetails.completeGameConfig
                            .roundFinishedOnLastCredit,
                        )
                      }
                    />
                  </Box>
                  <Typography className={styles.rotationConfigHelperText}>
                    &quot;Call CompleteGame On Credit Failure&quot; makes the
                    system call the platform's EndRound/CompleteGame endpoint
                    even when the final credit fails. &quot;Round Finished On
                    Last Credit&quot; is for platforms with no separate EndRound
                    endpoint (e.g. Tequity) - the last settling credit carries
                    the round-finished flag instead.
                  </Typography>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        </Box>
        <Divider />
        <Box className={styles.secondFormContainer}>
          {options.requiredMethods?.map((request) => {
            let requestOption = options.requiredMethodOptions.find(
              (e) => e.id === request,
            );
            return (
              <Box className={styles.requestContainer}>
                <Typography fontSize={12} fontWeight={700}>
                  {requestOption.label}
                </Typography>
                <Box className={styles.field}>
                  <SimpleTextInput
                    label={"URL"}
                    name={"url"}
                    value={platformDetails[request]?.url}
                    onChange={(e) => onRequestChange(e, request)}
                  />
                </Box>
                <Box className={styles.field}>
                  <SimpleSelectInput
                    name={"method"}
                    label={"Method Type"}
                    value={platformDetails[request]?.method}
                    options={options.httpMethodOptions}
                    onChange={(e) => onRequestChange(e, request)}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
      {/* Round ID Rotation Config Info Dialog */}
      <Dialog
        open={rotationInfoOpen}
        onClose={() => setRotationInfoOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Round ID Rotation Config</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" gutterBottom>
            Rotates the <b>numericRoundId</b> when the platform returns specific
            error codes consecutively. This helps recover from platform-side
            caching or conflict issues tied to a particular round ID.
          </Typography>
          <Typography variant="subtitle2" sx={{ mt: 2 }}>
            Fields:
          </Typography>
          <ul style={{ margin: "8px 0", paddingLeft: 20, fontSize: 14 }}>
            <li>
              <b>Enabled</b> &mdash; Toggle to enable/disable rotation logic.
            </li>
            <li>
              <b>Consecutive Error Threshold</b> &mdash; Number of consecutive
              retryable errors required before rotating the round ID.
            </li>
            <li>
              <b>Redis TTL (seconds)</b> &mdash; How long the error counter and
              round ID tracking keys persist in Redis.
            </li>
            <li>
              <b>Max Rotations Per Round</b> &mdash; Maximum number of times the
              round ID can be rotated for a single game round (prevents infinite
              loops).
            </li>
            <li>
              <b>Retryable Error Codes</b> &mdash; Platform-specific numeric
              error codes that should trigger rotation (e.g. 6, 407).
            </li>
          </ul>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRotationInfoOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Failure Retry Config Info Dialog */}
      <Dialog
        open={retryInfoOpen}
        onClose={() => setRetryInfoOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Failure Retry Config</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" gutterBottom>
            Retries the same bet request (same parameters, same numericRoundId)
            when the platform returns specific transient error codes. This runs{" "}
            <b>before</b> rotation logic &mdash; if all retries fail and the
            error code is also in the rotation config, rotation will then kick
            in with a new numericRoundId.
          </Typography>
          <Typography variant="subtitle2" sx={{ mt: 2 }}>
            Fields:
          </Typography>
          <ul style={{ margin: "8px 0", paddingLeft: 20, fontSize: 14 }}>
            <li>
              <b>Enabled</b> &mdash; Toggle to enable/disable retry logic.
            </li>
            <li>
              <b>Max Retries</b> &mdash; Number of retry attempts per individual
              bet call. Keep low (1-2 recommended).
            </li>
            <li>
              <b>Delay (ms)</b> &mdash; Fixed delay in milliseconds between
              retry attempts. Keep short (200-500ms recommended) to avoid ALB
              timeout and poor UX.
            </li>
            <li>
              <b>Bet Retry Error Codes</b> &mdash; Platform error codes that
              trigger bet retries.
            </li>
          </ul>
          <Typography variant="subtitle2" sx={{ mt: 2 }}>
            Interaction with Rotation:
          </Typography>
          <Typography variant="body2">
            Failure retry runs first with the same numericRoundId. If all
            retries are exhausted and the error is also configured in the Round
            ID Rotation Config, rotation logic will then generate a new
            numericRoundId and retry once more.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRetryInfoOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Generate Hex Secret Dialog */}
      <Dialog
        open={hexSecretDialogOpen}
        onClose={() => setHexSecretDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Generated Hex Secret</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" gutterBottom>
            Use this hex-encoded secret as the <b>signatureToken</b> in the
            platform&apos;s Extra Data and share the same value with the
            platform provider.
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mt: 2,
              p: 1.5,
              backgroundColor: "#f5f5f5",
              borderRadius: 1,
              border: "1px solid #ddd",
            }}
          >
            <Typography
              sx={{
                fontFamily: "monospace",
                fontSize: "13px",
                wordBreak: "break-all",
                flex: 1,
              }}
            >
              {generatedSecret}
            </Typography>
            <IconButton size="small" onClick={handleCopySecret} title="Copy">
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: "block", fontStyle: "italic" }}
          >
            This secret is generated once and not stored. Copy it before closing
            this dialog.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleGenerateSecret} disabled={generatingSecret}>
            Regenerate
          </Button>
          <Button onClick={() => setHexSecretDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </ContentShell>
  );
};

export default PlatformEditor;
